import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { updatePaymentStatus, showSuccessPopup, showFailurePopup, checkPaymentStatus } from '../services/paymentService';

interface CashfreeOrderResponse {
  cf_order_id: number;
  order_id: string;
  order_status: string;
  order_amount: number;
  order_currency: string;
  customer_details: {
    customer_id: string;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    customer_uid: string | null;
  };
  payment_session_id: string;
  order_meta: {
    return_url: string;
    notify_url: string | null;
    payment_methods: string | null;
  };
  created_at: string;
}

const PaymentStatusPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Log URL parameters for debugging
        console.log('Complete URL:', window.location.href);

        if (!orderId) {
          throw new Error('Order ID not found in URL');
        }

        // Get payment status from your API
        const paymentStatus: CashfreeOrderResponse = await checkPaymentStatus(orderId);
        console.log('Payment status details:', paymentStatus);

        if (!paymentStatus) {
          throw new Error('Failed to get payment status');
        }

        // Check if payment is successful (PAID status)
        const isSuccess = paymentStatus.order_status === 'PAID';

        console.log('Payment success check:', {
          orderStatus: paymentStatus.order_status,
          orderId: paymentStatus.order_id,
          amount: paymentStatus.order_amount,
          isSuccess: isSuccess
        });

        // Update payment status in your system
        debugger;

        // Show appropriate popup and redirect
        if (isSuccess) {
          console.log('Payment successful! Showing success popup');
          showSuccessPopup(orderId);
          navigate(`/order-summary/${orderId}`);
        } else {
          console.log('Payment failed! Showing failure popup');
          showFailurePopup(`Payment Failed`, orderId);
          navigate(`/order-summary/${orderId}`);
        }
      } catch (error) {
        console.error('Error processing payment status:', error);
        setError(error instanceof Error ? error.message : 'Failed to process payment');
        showFailurePopup('Error processing payment status', orderId || '');
        setTimeout(() => navigate(`/order-summary/${orderId}`), 3000);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [navigate, orderId]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Redirecting to order summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minto-500 mx-auto"></div>
        <p className="mt-4 text-gray-700">Verifying payment status...</p>
      </div>
    </div>
  );
};

export default PaymentStatusPage; 